import type {Graph} from 'src/types/chatbot';

import * as React from 'react';
import {useDispatch} from 'react-redux';

import {useChatbotStateContext} from '../chatbot-state-context';
import useI18n from 'src/hooks/useI18n';

import {pushModal} from 'src/action-creators/modal';

import {
  getAllEntities,
  getAllEntityAttributes,
} from 'src/action-creators/ats-entities';

import Loading from 'src/components/lib/loading';

import {Content} from 'src/components/modals/components';
import {Button} from 'src/designSystem2021Components/buttons';
import {TitleText} from '../common';

import QuestionNode from './question';
import MessageNode from './message';
import FileUploadNode from './file_upload';
import JobMatchNode from './job-match';
import ConditionNode from './condition';
import NotificationNode from './notification';
import SchedulerNode from './scheduler';
import DispositionNode from './disposition';
import SummaryNode from './summary';
import TextToApplyNode from './text-to-apply';
import {LATNode} from './lat';
import {LinkToFlowNode} from './link-to-flow';
import {DynamicQuestionNode} from './dynamic-question';

import {useAsyncDependency} from '../node-editor/editor';

import css from './node-editor-v2.css';


type NodeEditorSharedProps = {
  graph: Graph;
  onCancel: () => unknown;
  readOnly: boolean;
};

export type NodeEditorProps = NodeEditorSharedProps & {
  isSavingNode: boolean;
  setIsSavingNode: (arg1: boolean) => unknown;
};

const NodeEditorWrapper = (props: NodeEditorSharedProps) => {
  const reduxDispatch = useDispatch();

  const loading = useAsyncDependency(
    () =>
      Promise.all([
        reduxDispatch(getAllEntities()),
        reduxDispatch(getAllEntityAttributes()),
      ]),
    [],
  );

  return loading ? <Loading /> : <NodeEditor {...props} />;
};

export default NodeEditorWrapper;

function NodeEditor({graph, onCancel, readOnly}: NodeEditorSharedProps) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {state} = useChatbotStateContext();
  const reduxDispatch = useDispatch();

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const nodeType = state.insertingNode
    ? state.insertingNode.nodeType
    : editingNodeData?.type;

  const [isSavingNode, setIsSavingNode] = React.useState(false);

  const handleCancelModal = () => {
    if (readOnly) {
      onCancel();
    } else {
      reduxDispatch(
        pushModal({
          render: ({removeModal}) => (
            <CancelConfirmModal
              onCancel={() => {
                removeModal(); //removes this modeal
                onCancel(); //removes side panel
              }}
              onSave={() => {
                removeModal();
                setIsSavingNode(true);
              }}
            />
          ),
        }),
      );
    }
  };

  const nodeProps = {
    graph,
    onCancel: handleCancelModal,
    readOnly,
    isSavingNode,
    setIsSavingNode,
  } as const;
  if (!nodeType) {
    return <Loading />;
  }

  switch (nodeType) {
    case 'question':
      return (
        <div>
          <QuestionNode {...nodeProps} />
        </div>
      );

    case 'message':
      return <MessageNode {...nodeProps} />;
    case 'file_upload':
      return <FileUploadNode {...nodeProps} />;
    case 'notification':
      return <NotificationNode {...nodeProps} />;
    case 'scheduler':
      return <SchedulerNode {...nodeProps} />;
    case 'disposition':
      return <DispositionNode {...nodeProps} />;
    case 'job_match':
      return <JobMatchNode {...nodeProps} />;
    case 'condition':
      return <ConditionNode {...nodeProps} />;
    case 'summary':
      return <SummaryNode {...nodeProps} />;
    case 'live_agent_transfer':
      return <LATNode {...nodeProps} />;
    case 'link_to_flow':
      return <LinkToFlowNode {...nodeProps} />;
    case 'dynamic_question':
      return <DynamicQuestionNode {...nodeProps} />;
    case 'text_to_apply':
      return <TextToApplyNode {...nodeProps} />;
    default:
      return <div>{t('error', 'error')}</div>;
  }
}

// @ts-expect-error - TS7031 - Binding element 'onSave' implicitly has an 'any' type. | TS7031 - Binding element 'onCancel' implicitly has an 'any' type.
function CancelConfirmModal({onSave, onCancel}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    // @ts-expect-error TS2746
    <Content>
      <TitleText className={css.title}>
        {t('No_Changes_Saved', 'No Changes Saved')}
      </TitleText>
      <p>{`${t(
        'You_havent_saved_your_changes_yet_If_you_continue_all_edits_and_changes_to_this_node_will_be_lost_We_recommend_saving_before_navigating_out_of_editor_mode',
        'You haven’t saved your changes yet. If you continue, all edits and changes to this node will be lost. We recommend saving before navigating out of editor mode',
      )}.`}</p>
      <p>{`${t(
        'Are_you_sure_you_want_to_proceed',
        'Are you sure you want to proceed',
      )}?`}</p>

      <div className={css.cancelConfirmControls}>
        <Button
          className={css.button}
          type="text"
          size="standard"
          onClick={onCancel}
        >
          {t('Continue_without_saving', 'Continue without saving')}
        </Button>
        <Button
          className={css.button}
          type="primary"
          size="standard"
          onClick={onSave}
        >
          {t('Save_changes', 'Save changes')}
        </Button>
      </div>
    </Content>
  );
}
